import { ChangeDetectionStrategy, Component, computed, forwardRef, input, output, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ChipComponent } from 'src/app/ui-kit/chip/chip.component';
import { InputLabelComponent } from 'src/app/ui-kit/form-controls/input-label/input-label.component';

/** ISO date range (YYYY-MM-DD strings). */
export interface DateRange {
  from: string | null;
  to: string | null;
}

/** Preset option rendered as a chip above the inputs. */
export interface DateRangePreset {
  label: string;
  /** Factory returning the range for "now". Called each time the preset is clicked. */
  range: () => DateRange;
}

/** Default presets: Today, Last 7 days, This month, This quarter. */
export const DEFAULT_DATE_RANGE_PRESETS: DateRangePreset[] = [
  {
    label: 'Today',
    range: () => {
      const today = new Date().toISOString().slice(0, 10);
      return { from: today, to: today };
    },
  },
  {
    label: 'Last 7 days',
    range: () => {
      const to = new Date();
      const from = new Date();
      from.setDate(to.getDate() - 6);
      return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
    },
  },
  {
    label: 'This month',
    range: () => {
      const now = new Date();
      const from = new Date(now.getFullYear(), now.getMonth(), 1);
      const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
    },
  },
  {
    label: 'This quarter',
    range: () => {
      const now = new Date();
      const q = Math.floor(now.getMonth() / 3);
      const from = new Date(now.getFullYear(), q * 3, 1);
      const to = new Date(now.getFullYear(), q * 3 + 3, 0);
      return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
    },
  },
];

/**
 * Date range picker. Two native `<input type="date">` elements with optional preset
 * chips. CVA-compliant — works with reactive forms where the value is a `DateRange`.
 *
 * For single date selection, use `DatePickerComponent`.
 *
 * @example
 * <app-date-range-picker
 *   label="Period"
 *   [presets]="defaultPresets"
 *   [formControl]="rangeCtrl"
 * />
 *
 * @feature shared
 */
@Component({
  selector: 'app-date-range-picker',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [InputLabelComponent, ChipComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateRangePickerComponent),
      multi: true,
    },
  ],
  host: { class: 'block' },
  template: `
    @if (label()) {
      <app-input-label [required]="required()">{{ label() }}</app-input-label>
    }

    @if (presets().length) {
      <div class="mt-1 flex flex-wrap gap-2">
        @for (preset of presets(); track preset.label) {
          <app-chip
            color="purple"
            [outline]="activePresetLabel() !== preset.label"
            [clickable]="true"
            size="sm"
            (click)="applyPreset(preset)"
          >
            {{ preset.label }}
          </app-chip>
        }
      </div>
    }

    <div class="mt-2 flex flex-wrap items-center gap-2">
      <input
        type="date"
        class="rounded border border-outline bg-surface px-3 py-2 text-body focus-visible:outline-none focus-visible:shadow-[var(--theme-focus-ring)]"
        [value]="fromValue()"
        [max]="toValue() ?? null"
        (input)="onFromInput($event)"
        (blur)="onBlur()"
        [attr.aria-label]="'Start date'"
      />
      <span class="text-muted">–</span>
      <input
        type="date"
        class="rounded border border-outline bg-surface px-3 py-2 text-body focus-visible:outline-none focus-visible:shadow-[var(--theme-focus-ring)]"
        [value]="toValue()"
        [min]="fromValue() ?? null"
        (input)="onToInput($event)"
        (blur)="onBlur()"
        [attr.aria-label]="'End date'"
      />
    </div>

    @if (errorText()) {
      <p class="mt-1 text-sm text-danger" role="alert" aria-live="polite">{{ errorText() }}</p>
    } @else if (helperText()) {
      <p class="mt-1 text-sm text-muted">{{ helperText() }}</p>
    }
  `,
})
export class DateRangePickerComponent implements ControlValueAccessor {
  readonly label = input<string | undefined>(undefined);
  readonly required = input<boolean>(false);
  readonly helperText = input<string | undefined>(undefined);
  readonly errorText = input<string | undefined>(undefined);

  /** Preset chips shown above the inputs. Pass `[]` to hide. */
  readonly presets = input<DateRangePreset[]>(DEFAULT_DATE_RANGE_PRESETS);

  /** Fires on any range change in addition to the CVA callback. */
  readonly rangeChange = output<DateRange>();

  private readonly value = signal<DateRange>({ from: null, to: null });
  readonly fromValue = computed(() => this.value().from);
  readonly toValue = computed(() => this.value().to);

  /** Label of the preset whose range matches the current value, if any. */
  readonly activePresetLabel = computed(() => {
    const v = this.value();
    if (!v.from && !v.to) return null;
    for (const preset of this.presets()) {
      const r = preset.range();
      if (r.from === v.from && r.to === v.to) return preset.label;
    }
    return null;
  });

  private onChange: (v: DateRange) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: DateRange | null): void {
    this.value.set(value ?? { from: null, to: null });
  }

  registerOnChange(fn: (v: DateRange) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(_isDisabled: boolean): void {
    // No-op for this minimal wrapper; consumers disable via caller state.
  }

  onFromInput(event: Event): void {
    const from = (event.target as HTMLInputElement).value || null;
    this.update({ ...this.value(), from });
  }

  onToInput(event: Event): void {
    const to = (event.target as HTMLInputElement).value || null;
    this.update({ ...this.value(), to });
  }

  onBlur(): void {
    this.onTouched();
  }

  applyPreset(preset: DateRangePreset): void {
    this.update(preset.range());
  }

  private update(next: DateRange): void {
    this.value.set(next);
    this.onChange(next);
    this.rangeChange.emit(next);
  }
}
